import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import ConfirmModal from "../../common/ConfirmModal";
import ReportTable from "../../common/ReportTable/ReportTable";
import DateRangeFilter from "../../common/DateRangeFilter";
import IsfPageTour from "../isf/IsfPageTour";
import "../isf/IsfPageTour.scss";
import utils from "../../../utils/utils";
import QmsModalPortal from "./QmsModalPortal";
import { buildQmsQuizBuilderTourSteps } from "./qmsQuizBuilderTourSteps";
import { buildQmsQuizListTourSteps } from "./qmsQuizListTourSteps";
import {
  hasCompletedQmsQuizBuilderTour,
  markQmsQuizBuilderTourCompleted,
} from "./qmsQuizBuilderTourStorage";
import {
  hasCompletedQmsQuizListTour,
  markQmsQuizListTourCompleted,
} from "./qmsQuizManagerTourStorage";
import {
  fetchQuizzesV2,
  fetchQuizV2,
  fetchAvailableQuestionsV2,
  fetchQuestionV2,
  saveQuizV2,
  deleteQuizV2,
} from "./qmsService";
import {
  firstSuccessStoryRow,
  getApiTable,
  isApiSuccess,
  apiMessage,
} from "./qmsApiUtils";
import { useQmsAccess } from "./qmsPermissionUtils";
import {
  QMS_COLUMN_DATE_PRESETS,
  QMS_EMPTY_DATE_RANGE,
  matchesQmsDateRange,
} from "./qmsColumnDateFilterUtils";

const emptyQuizForm = () => ({
  QuizName: "",
  PassingPercentage: 100,
  AttemptLimit: 0,
  QuestionsToServeCount: 0,
});

const QUIZ_FIELD_OPTIONS = [
  { key: "QuizName", label: "Quiz name" },
  { key: "PassingPercentage", label: "Pass %" },
  { key: "MappedQuestionCount", label: "Mapped questions" },
  { key: "QuestionsToServeCount", label: "Serve per attempt" },
  { key: "DocumentBindingCount", label: "Document bindings" },
  { key: "CreatedAt", label: "Created" },
];

const DEFAULT_QUIZ_COLUMNS = [
  "QuizName",
  "PassingPercentage",
  "MappedQuestionCount",
  "QuestionsToServeCount",
  "DocumentBindingCount",
  "CreatedAt",
];

const digitsOnly = (value) => String(value ?? "").replace(/\D/g, "");

const QuizNumericInput = ({
  value,
  onChange,
  disabled = false,
  className = "form-control",
  placeholder = "",
  max,
}) => {
  const displayValue = value > 0 ? String(value) : value === 0 ? "0" : "";

  const handleChange = (event) => {
    const digits = digitsOnly(event.target.value);
    if (!digits) {
      onChange(0);
      return;
    }

    let num = parseInt(digits, 10);
    if (Number.isInteger(max) && max > 0) {
      num = Math.min(num, max);
    }
    onChange(num);
  };

  return (
    <input
      type="text"
      inputMode="numeric"
      autoComplete="off"
      className={className}
      value={displayValue}
      placeholder={placeholder}
      disabled={disabled}
      onChange={handleChange}
    />
  );
};

const QuizOptionalNumericInput = ({
  value,
  onChange,
  disabled = false,
  className = "form-control",
  placeholder = "",
  max,
}) => {
  const displayValue = value > 0 ? String(value) : "";

  const handleChange = (event) => {
    const digits = digitsOnly(event.target.value);
    if (!digits) {
      onChange(0);
      return;
    }

    let num = parseInt(digits, 10);
    if (Number.isInteger(max) && max > 0) {
      num = Math.min(num, max);
    }
    onChange(num);
  };

  return (
    <input
      type="text"
      inputMode="numeric"
      autoComplete="off"
      className={className}
      value={displayValue}
      placeholder={placeholder}
      disabled={disabled}
      onChange={handleChange}
    />
  );
};

const QuizAssignQuestionRow = ({
  question,
  isAssigned = false,
  isExpanded = false,
  options = [],
  optionsLoading = false,
  optionsError = "",
  onToggleOptions,
  onAction,
  showOptionsTour = false,
  actionDisabled = false,
}) => {
  const optionCount = Number(question.OptionCount) || 0;
  const optionsToggleLabel = isExpanded
    ? "Hide options"
    : optionCount > 0
      ? `View ${optionCount} options`
      : "View options";

  return (
    <div
      className={`qms-quiz-builder-modal__question-row${
        isAssigned ? " is-assigned" : ""
      }${isExpanded ? " is-expanded" : ""}`}
    >
      <div className="qms-quiz-builder-modal__question-body">
        <span className="qms-quiz-builder-modal__question-text">
          {question.QuestionText || question.QuestionPreview}
        </span>
        <button
          type="button"
          className="qms-quiz-builder-modal__options-toggle"
          aria-expanded={isExpanded}
          data-tour={showOptionsTour ? "qms-quiz-builder-options-toggle" : undefined}
          onClick={() => onToggleOptions(question.QuestionId)}
        >
          {optionsToggleLabel}
        </button>
        {isExpanded && (
          <div className="qms-quiz-builder-modal__question-options">
            {optionsLoading && (
              <div className="qms-quiz-builder-modal__options-loading">Loading options...</div>
            )}
            {optionsError && <div className="text-danger small">{optionsError}</div>}
            {!optionsLoading && !optionsError && options.length > 0 && (
              <ul className="qms-quiz-builder-modal__options-list">
                {options.map((option, idx) => {
                  const isCorrect = option.IsCorrect === true || option.IsCorrect === 1;
                  return (
                    <li
                      key={option.OptionId || `${question.QuestionId}-${idx}`}
                      className={isCorrect ? "is-correct" : undefined}
                    >
                      <span className="qms-quiz-builder-modal__option-label">
                        {option.SerialNumber || idx + 1}.
                      </span>
                      <span className="qms-quiz-builder-modal__option-text">
                        {option.OptionText}
                      </span>
                      {isCorrect && (
                        <span className="qms-quiz-builder-modal__option-correct">Correct</span>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
            {!optionsLoading && !optionsError && options.length === 0 && (
              <div className="qms-quiz-builder-modal__options-empty">No options found.</div>
            )}
          </div>
        )}
      </div>
      <button
        type="button"
        className={`qms-quiz-builder-modal__row-action ${
          isAssigned
            ? "qms-quiz-builder-modal__row-action--remove"
            : "qms-quiz-builder-modal__row-action--add"
        }`}
        title={
          actionDisabled
            ? "Read-only"
            : isAssigned
              ? "Remove from quiz"
              : "Add to quiz"
        }
        aria-label={isAssigned ? "Remove from quiz" : "Add to quiz"}
        disabled={actionDisabled}
        onClick={() => onAction(question.QuestionId)}
      >
        {isAssigned ? "−" : "+"}
      </button>
    </div>
  );
};

const QuizAssignListToolbar = ({
  title,
  count,
  searchValue,
  searchPlaceholder,
  onSearchChange,
  bulkLabel,
  bulkClassName,
  onBulk,
  bulkDisabled,
  dataTour,
}) => (
  <div
    className="qms-quiz-builder-modal__list-toolbar"
    data-tour={dataTour || undefined}
  >
    <div className="qms-quiz-builder-modal__list-toolbar-row">
      <div className="qms-quiz-builder-modal__list-title-wrap">
        <span className="qms-quiz-builder-modal__list-title">{title}</span>
        <span className="qms-quiz-builder-modal__list-count">{count} total</span>
      </div>
      <div className="qms-quiz-builder-modal__list-search-wrap">
        <i className="fas fa-search qms-quiz-builder-modal__list-search-icon" aria-hidden="true" />
        <input
          type="search"
          className="qms-quiz-builder-modal__list-search"
          placeholder={searchPlaceholder}
          value={searchValue}
          onChange={(e) => onSearchChange(e.target.value)}
        />
      </div>
      <button
        type="button"
        className={`qms-quiz-builder-modal__bulk-btn ${bulkClassName}`}
        onClick={onBulk}
        disabled={bulkDisabled}
      >
        {bulkLabel}
      </button>
    </div>
  </div>
);

const QmsQuizBuilderPanel = ({
  userData,
  showLoader,
  hideLoader,
  showAlert,
  questionBankRevision = 0,
  onGoToQuestionsTab,
  actionsSlot = null,
}) => {
  const { canQmsQuizAddEdit, canQmsQuizDelete } = useQmsAccess();
  const [rows, setRows] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [createdDateRange, setCreatedDateRange] = useState(QMS_EMPTY_DATE_RANGE);
  const [showModal, setShowModal] = useState(false);
  const [editingQuizId, setEditingQuizId] = useState("");
  const [form, setForm] = useState(emptyQuizForm());
  const [availableQuestions, setAvailableQuestions] = useState([]);
  const [selectedQuestionIds, setSelectedQuestionIds] = useState([]);
  const [errors, setErrors] = useState({});
  const [confirmModal, setConfirmModal] = useState({ open: false, row: null });
  const [assignedSearch, setAssignedSearch] = useState("");
  const [unassignedSearch, setUnassignedSearch] = useState("");
  const [expandedQuestionIds, setExpandedQuestionIds] = useState({});
  const [questionOptionsCache, setQuestionOptionsCache] = useState({});
  const [questionOptionsLoading, setQuestionOptionsLoading] = useState({});
  const [questionOptionsError, setQuestionOptionsError] = useState({});
  const [showQuizTour, setShowQuizTour] = useState(false);
  const [showListTour, setShowListTour] = useState(false);
  const [isReadOnly, setIsReadOnly] = useState(false);

  const hasAssignmentQuestions = availableQuestions.length > 0;

  const quizTourSteps = useMemo(
    () => buildQmsQuizBuilderTourSteps({ hasQuestions: hasAssignmentQuestions }),
    [hasAssignmentQuestions],
  );

  const quizListTourSteps = useMemo(() => buildQmsQuizListTourSteps(), []);

  const mappedCount = selectedQuestionIds.length;
  const assignedSearchTerm = assignedSearch.trim().toLowerCase();
  const unassignedSearchTerm = unassignedSearch.trim().toLowerCase();
  const selectedIdSet = useMemo(
    () => new Set(selectedQuestionIds),
    [selectedQuestionIds],
  );

  const questionById = useMemo(() => {
    const map = new Map();
    availableQuestions.forEach((q) => map.set(q.QuestionId, q));
    return map;
  }, [availableQuestions]);

  const assignedQuestions = useMemo(
    () =>
      selectedQuestionIds
        .map((id) => questionById.get(id))
        .filter(Boolean)
        .filter((q) => {
          if (!assignedSearchTerm) return true;
          const text = String(q.QuestionText || q.QuestionPreview || "").toLowerCase();
          return text.includes(assignedSearchTerm);
        }),
    [selectedQuestionIds, questionById, assignedSearchTerm],
  );

  const unassignedQuestions = useMemo(
    () =>
      availableQuestions.filter((q) => {
        if (selectedIdSet.has(q.QuestionId)) return false;
        if (!unassignedSearchTerm) return true;
        const text = String(q.QuestionText || q.QuestionPreview || "").toLowerCase();
        return text.includes(unassignedSearchTerm);
      }),
    [availableQuestions, selectedIdSet, unassignedSearchTerm],
  );

  const serveCountValue = useMemo(() => {
    if (!form.QuestionsToServeCount || form.QuestionsToServeCount <= 0) {
      return mappedCount || 0;
    }
    return Math.min(form.QuestionsToServeCount, mappedCount);
  }, [form.QuestionsToServeCount, mappedCount]);

  const resetForm = () => {
    setEditingQuizId("");
    setForm(emptyQuizForm());
    setSelectedQuestionIds([]);
    setAssignedSearch("");
    setUnassignedSearch("");
    setExpandedQuestionIds({});
    setQuestionOptionsCache({});
    setQuestionOptionsLoading({});
    setQuestionOptionsError({});
    setErrors({});
    setIsReadOnly(false);
  };

  const loadAvailableQuestions = async () => {
    if (!userData?.OrganizationId) return;
    const response = await fetchAvailableQuestionsV2(userData);
    setAvailableQuestions(getApiTable(response, "AvailableQuestions"));
  };

  const loadQuizzes = async () => {
    if (!userData?.OrganizationId || !userData?.StaffId) return;
    try {
      setListLoading(true);
      const response = await fetchQuizzesV2(userData);
      setRows(getApiTable(response, "Quizzes"));
    } catch (error) {
      showAlert({
        title: "Error",
        message: error?.message || "Failed to load quizzes.",
        type: "danger",
      });
      setRows([]);
    } finally {
      setListLoading(false);
    }
  };

  useEffect(() => {
    if (userData?.OrganizationId && userData?.StaffId) {
      loadQuizzes();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData?.OrganizationId, userData?.StaffId]);

  useEffect(() => {
    if (!userData?.StaffId) return undefined;
    if (hasCompletedQmsQuizListTour(userData.StaffId)) return undefined;
    const timer = window.setTimeout(() => setShowListTour(true), 500);
    return () => window.clearTimeout(timer);
  }, [userData?.StaffId]);

  useEffect(() => {
    if (userData?.OrganizationId && showModal) {
      loadAvailableQuestions().catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionBankRevision, userData?.OrganizationId, showModal]);

  useEffect(() => {
    if (!showModal || !userData?.StaffId) {
      setShowQuizTour(false);
      return undefined;
    }

    if (hasCompletedQmsQuizBuilderTour(userData.StaffId)) {
      return undefined;
    }

    const timer = window.setTimeout(() => setShowQuizTour(true), 450);
    return () => window.clearTimeout(timer);
  }, [showModal, userData?.StaffId]);

  const denyQuizAction = (actionLabel) => {
    showAlert({
      title: "Permission denied",
      message: `You do not have permission to ${actionLabel}. Ask your administrator to grant the required Quiz Manager action permissions.`,
      type: "warning",
    });
  };

  const openAddModal = async () => {
    if (!canQmsQuizAddEdit) {
      denyQuizAction("add quizzes");
      return;
    }
    resetForm();
    try {
      showLoader("Loading questions...");
      await loadAvailableQuestions();
      setShowModal(true);
    } catch (error) {
      showAlert({
        title: "Error",
        message: error?.message || "Failed to load question master.",
        type: "danger",
      });
    } finally {
      hideLoader();
    }
  };

  const openEditModal = async (row, { readOnly = false } = {}) => {
    if (!canQmsQuizAddEdit) {
      denyQuizAction("edit quizzes");
      return;
    }
    try {
      showLoader("Loading quiz...");
      const quizResponse = await fetchQuizV2(userData, row.QuizId);
      await loadAvailableQuestions();
      const quizRows = getApiTable(quizResponse, "Quiz");
      const mappedRows = getApiTable(quizResponse, "QuizQuestions");
      const quiz = quizRows[0] || row;

      setEditingQuizId(quiz.QuizId);
      setForm({
        QuizName: quiz.QuizName || "",
        PassingPercentage: Number(quiz.PassingPercentage) || 100,
        AttemptLimit: Number(quiz.AttemptLimit) || 0,
        QuestionsToServeCount: Number(quiz.QuestionsToServeCount) || 0,
      });
      setSelectedQuestionIds(mappedRows.map((q) => q.QuestionId));
      setErrors({});
      setIsReadOnly(readOnly);
      setShowModal(true);
    } catch (error) {
      showAlert({
        title: "Error",
        message: error?.message || "Failed to load quiz.",
        type: "danger",
      });
    } finally {
      hideLoader();
    }
  };

  const validate = () => {
    const next = {};
    const quizName = form.QuizName.trim();
    if (!quizName) {
      next.QuizName = "Quiz name is required.";
    } else {
      const duplicate = rows.some((row) => {
        const existingName = String(row?.QuizName || "").trim().toLowerCase();
        if (!existingName || existingName !== quizName.toLowerCase()) return false;
        if (editingQuizId && String(row?.QuizId) === String(editingQuizId)) {
          return false;
        }
        return true;
      });
      if (duplicate) {
        next.QuizName = "A quiz with this name already exists. Please use a unique name.";
      }
    }
    if (selectedQuestionIds.length < 1) {
      next.questions = "Select at least one question from question master.";
    }
    if (
      form.PassingPercentage < 1 ||
      form.PassingPercentage > 100 ||
      Number.isNaN(form.PassingPercentage)
    ) {
      next.PassingPercentage = "Passing percentage must be between 1 and 100.";
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const assignQuestion = (questionId) => {
    setSelectedQuestionIds((prev) =>
      prev.includes(questionId) ? prev : [...prev, questionId],
    );
  };

  const unassignQuestion = (questionId) => {
    setSelectedQuestionIds((prev) => prev.filter((id) => id !== questionId));
  };

  const assignAllUnassigned = () => {
    setSelectedQuestionIds((prev) => {
      const next = new Set(prev);
      availableQuestions.forEach((q) => {
        if (!next.has(q.QuestionId)) next.add(q.QuestionId);
      });
      return Array.from(next);
    });
  };

  const unassignAllAssigned = () => {
    setSelectedQuestionIds([]);
  };

  const toggleQuestionOptions = async (questionId) => {
    if (expandedQuestionIds[questionId]) {
      setExpandedQuestionIds((prev) => {
        const next = { ...prev };
        delete next[questionId];
        return next;
      });
      return;
    }

    setExpandedQuestionIds((prev) => ({ ...prev, [questionId]: true }));

    if (questionOptionsCache[questionId]) return;

    setQuestionOptionsLoading((prev) => ({ ...prev, [questionId]: true }));
    setQuestionOptionsError((prev) => {
      const next = { ...prev };
      delete next[questionId];
      return next;
    });

    try {
      const response = await fetchQuestionV2(userData, questionId);
      const optionRows = getApiTable(response, "QuestionOptions");
      setQuestionOptionsCache((prev) => ({ ...prev, [questionId]: optionRows }));
    } catch (error) {
      setQuestionOptionsError((prev) => ({
        ...prev,
        [questionId]: error?.message || "Could not load options.",
      }));
    } finally {
      setQuestionOptionsLoading((prev) => {
        const next = { ...prev };
        delete next[questionId];
        return next;
      });
    }
  };

  const getQuestionRowProps = (question) => ({
    question,
    isExpanded: Boolean(expandedQuestionIds[question.QuestionId]),
    options: questionOptionsCache[question.QuestionId] || [],
    optionsLoading: Boolean(questionOptionsLoading[question.QuestionId]),
    optionsError: questionOptionsError[question.QuestionId] || "",
    onToggleOptions: toggleQuestionOptions,
  });

  const handleSave = async () => {
    if (isReadOnly) return;
    if (!canQmsQuizAddEdit) {
      denyQuizAction(editingQuizId ? "edit quizzes" : "add quizzes");
      return;
    }
    if (!validate() || !userData?.OrganizationId) return;

    try {
      showLoader(editingQuizId ? "Updating quiz..." : "Saving quiz...");
      const response = await saveQuizV2(userData, {
        QuizId: editingQuizId || "",
        QuizName: form.QuizName.trim(),
        PassingPercentage: Number(form.PassingPercentage),
        AttemptLimit: Number(form.AttemptLimit) || 0,
        QuestionsToServeCount: serveCountValue,
        QuestionIdsCsv: selectedQuestionIds.join(","),
      });
      const row = firstSuccessStoryRow(response);

      if (isApiSuccess(response, row)) {
        showAlert({
          title: "Success",
          message: apiMessage(response, row) || "Quiz saved successfully.",
          type: "success",
        });
        setShowModal(false);
        resetForm();
        await loadQuizzes();
      } else {
        const message = apiMessage(response, row) || "Could not save quiz.";
        if (/unique quiz name|already exists/i.test(message)) {
          setErrors((prev) => ({ ...prev, QuizName: message }));
        }
        showAlert({
          title: "Error",
          message,
          type: "danger",
        });
      }
    } catch (error) {
      showAlert({
        title: "Error",
        message: error?.message || "Failed to save quiz.",
        type: "danger",
      });
    } finally {
      hideLoader();
    }
  };

  const handleEditClick = (row) => {
    if (!canQmsQuizAddEdit) {
      denyQuizAction("edit quizzes");
      return;
    }
    openEditModal(row, { readOnly: Number(row.DocumentBindingCount) > 0 });
  };

  const handleDeleteClick = (row) => {
    if (!canQmsQuizDelete) {
      denyQuizAction("delete quizzes");
      return;
    }
    if (Number(row.DocumentBindingCount) > 0) {
      showAlert({
        title: "Cannot delete",
        message: "This quiz is bound to a document and cannot be deleted.",
        type: "warning",
      });
      return;
    }
    setConfirmModal({ open: true, row });
  };

  const handleDeleteConfirm = async () => {
    const row = confirmModal.row;
    if (!row) {
      setConfirmModal({ open: false, row: null });
      return;
    }
    if (!canQmsQuizDelete) {
      denyQuizAction("delete quizzes");
      setConfirmModal({ open: false, row: null });
      return;
    }

    try {
      showLoader("Deleting quiz...");
      const response = await deleteQuizV2(userData, row.QuizId);
      const resRow = firstSuccessStoryRow(response);
      if (isApiSuccess(response, resRow)) {
        showAlert({
          title: "Deleted",
          message: apiMessage(response, resRow) || "Quiz deleted.",
          type: "success",
        });
        await loadQuizzes();
      } else {
        showAlert({
          title: "Error",
          message: apiMessage(response, resRow) || "Could not delete quiz.",
          type: "danger",
        });
      }
    } catch (error) {
      showAlert({
        title: "Error",
        message: error?.message || "Failed to delete quiz.",
        type: "danger",
      });
    } finally {
      hideLoader();
      setConfirmModal({ open: false, row: null });
    }
  };

  const tableRows = useMemo(
    () =>
      (rows || []).filter((row) => matchesQmsDateRange(row?.CreatedAt, createdDateRange)),
    [createdDateRange, rows],
  );

  const renderReportCell = useCallback(
    (row, field) => {
      if (field === "QuizName") {
        const name = String(row?.QuizName || "").trim() || "—";
        if (!canQmsQuizAddEdit) {
          return (
            <span className="qms-quiz-hub__quiz-name" title={name}>
              {name}
            </span>
          );
        }
        return (
          <button
            type="button"
            className="report-table__cell-link qms-quiz-hub__quiz-name-link"
            onClick={() => handleEditClick(row)}
            title={name}
          >
            {name}
          </button>
        );
      }
      if (field === "CreatedAt") {
        return utils.formatDateTimeDisplay(row.CreatedAt) || "—";
      }
      if (
        field === "PassingPercentage" ||
        field === "MappedQuestionCount" ||
        field === "QuestionsToServeCount" ||
        field === "DocumentBindingCount"
      ) {
        const value = row?.[field];
        return value == null || value === "" ? "—" : String(value);
      }
      return String(row?.[field] ?? "—");
    },
    [canQmsQuizAddEdit, handleEditClick],
  );

  const matchColumnSearch = useCallback((row, field, searchValue) => {
    if (field === "CreatedAt") return true;
    const raw = String(searchValue || "").trim();
    if (!raw) return true;
    return String(row?.[field] ?? "")
      .toLowerCase()
      .includes(raw.toLowerCase());
  }, []);

  const renderColumnSearch = useCallback(
    (field, options) => {
      if (field === "CreatedAt") {
        if (options?.checkOnly) return true;
        return (
          <div className="qms-doc-lib__column-date-filter">
            <DateRangeFilter
              value={createdDateRange}
              onChange={setCreatedDateRange}
              placeholder="Created range"
              presetOptions={QMS_COLUMN_DATE_PRESETS}
              compact
            />
          </div>
        );
      }
      return null;
    },
    [createdDateRange],
  );

  const tableActions = useMemo(
    () =>
      canQmsQuizDelete
        ? [
            {
              label: "Delete",
              icon: "fas fa-trash-alt",
              onClick: handleDeleteClick,
            },
          ]
        : null,
    [canQmsQuizDelete, handleDeleteClick],
  );

  const quizDefaultColumns = useMemo(
    () =>
      canQmsQuizDelete
        ? [...DEFAULT_QUIZ_COLUMNS, "rowActions"]
        : DEFAULT_QUIZ_COLUMNS,
    [canQmsQuizDelete],
  );

  const layoutStorageKey = useMemo(
    () =>
      userData?.StaffId ? `qms-quiz-manager-quizzes-layout-v5-${userData.StaffId}` : "",
    [userData?.StaffId],
  );

  const headerActions = (
    <div className="qms-quiz-hub__header-actions" data-tour="qms-quiz-list-toolbar">
      <button
        type="button"
        className="qms-tour-help-btn"
        data-tour="qms-quiz-list-guide"
        title="Show quiz list guide"
        onClick={() => setShowListTour(true)}
      >
        <i className="fas fa-circle-question me-1" aria-hidden="true" />
        Guide
      </button>
      <button
        type="button"
        className="btn btn-outline-secondary btn-sm"
        onClick={loadQuizzes}
      >
        <i className="fas fa-refresh me-1" aria-hidden="true" />
        Refresh
      </button>
      {canQmsQuizAddEdit ? (
        <button type="button" className="btn btn-primary btn-sm" onClick={openAddModal}>
          <i className="fas fa-plus me-1" aria-hidden="true" />
          Add quiz
        </button>
      ) : null}
    </div>
  );

  return (
    <>
      {actionsSlot ? createPortal(headerActions, actionsSlot) : null}

      <div className="qms-doc-lib__report-wrap" data-tour="qms-quiz-list-table">
        <ReportTable
          rows={tableRows}
          fieldOptions={QUIZ_FIELD_OPTIONS}
          defaultColumns={quizDefaultColumns}
          renderCell={renderReportCell}
          renderColumnSearch={renderColumnSearch}
          matchColumnSearch={matchColumnSearch}
          pageSizeOptions={[10, 25, 50, 100]}
          columnSearchMode="client"
          rowActions={tableActions}
          persistLayoutKey={layoutStorageKey}
          loading={listLoading}
        />
      </div>

      <IsfPageTour
        open={showListTour}
        steps={quizListTourSteps}
        staffId={userData?.StaffId || ""}
        markTourCompleted={markQmsQuizListTourCompleted}
        onClose={() => setShowListTour(false)}
      />

      <ConfirmModal
        isOpen={confirmModal.open}
        onClose={() => setConfirmModal({ open: false, row: null })}
        onConfirm={handleDeleteConfirm}
        title="Delete quiz"
        message={
          confirmModal.row ? `Delete quiz "${confirmModal.row.QuizName}"?` : ""
        }
        confirmText="Yes"
        cancelText="No"
        confirmButtonClass="btn-danger"
      />

      {showModal && (
        <QmsModalPortal>
          <div
            className="modal fade show d-block qms-quiz-hub-modal qms-quiz-builder-modal"
            tabIndex="-1"
            role="dialog"
            aria-modal="true"
          >
            <div className="modal-dialog qms-quiz-builder-modal__dialog">
              <div className="modal-content qms-quiz-builder-modal__content">
                <div className="modal-header qms-quiz-builder-modal__header py-2">
                  <div>
                    <h5 className="modal-title mb-0">
                      {editingQuizId
                        ? isReadOnly
                          ? "View quiz"
                          : "Edit quiz"
                        : "Add quiz"}
                    </h5>
                    <div className="qms-quiz-builder-modal__subtitle">
                      {isReadOnly
                        ? "Read-only — this quiz is bound to a document"
                        : "Settings on the left · assign questions on the right · open Guide for help"}
                    </div>
                  </div>
                  <div className="qms-quiz-builder-modal__header-actions">
                    <button
                      type="button"
                      className="qms-tour-help-btn qms-quiz-builder-modal__guide-btn"
                      data-tour="qms-quiz-builder-guide"
                      title="Show quiz builder guide"
                      onClick={() => setShowQuizTour(true)}
                    >
                      <i className="fas fa-circle-question" aria-hidden="true" />
                      Guide
                    </button>
                    <button
                      type="button"
                      className="btn-close"
                      onClick={() => {
                        setShowModal(false);
                        resetForm();
                      }}
                    />
                  </div>
                </div>

                {isReadOnly ? (
                  <div
                    className="qms-quiz-hub__inline-alert qms-quiz-builder-modal__inline-alert"
                    role="status"
                  >
                    <i className="fas fa-info-circle" aria-hidden="true" />
                    <span>
                      This quiz is bound to a document and cannot be edited. Staff may have
                      already passed or acknowledged. Create a new quiz for changes.
                    </span>
                  </div>
                ) : null}

                <div className="qms-quiz-builder-modal__body">
                  <aside
                    className="qms-quiz-builder-modal__form-pane"
                    data-tour="qms-quiz-builder-form"
                  >
                    <div className="qms-quiz-builder-modal__pane-title">Quiz settings</div>

                    <div className="mb-3">
                      <label className="form-label">Quiz name</label>
                      <input
                        type="text"
                        className={`form-control ${errors.QuizName ? "is-invalid" : ""}`}
                        value={form.QuizName}
                        disabled={isReadOnly}
                        onChange={(e) =>
                          setForm((f) => ({ ...f, QuizName: e.target.value }))
                        }
                      />
                      {errors.QuizName && (
                        <div className="invalid-feedback d-block">{errors.QuizName}</div>
                      )}
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Passing %</label>
                      <input
                        type="number"
                        min={1}
                        max={100}
                        className={`form-control ${errors.PassingPercentage ? "is-invalid" : ""}`}
                        value={form.PassingPercentage}
                        disabled={isReadOnly}
                        onChange={(e) =>
                          setForm((f) => ({
                            ...f,
                            PassingPercentage: Number(e.target.value),
                          }))
                        }
                      />
                      {errors.PassingPercentage && (
                        <div className="invalid-feedback d-block">
                          {errors.PassingPercentage}
                        </div>
                      )}
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Attempt limit (0 = unlimited)</label>
                      <QuizNumericInput
                        value={form.AttemptLimit}
                        disabled={isReadOnly}
                        onChange={(num) =>
                          setForm((f) => ({
                            ...f,
                            AttemptLimit: num,
                          }))
                        }
                      />
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Questions per attempt</label>
                      <QuizOptionalNumericInput
                        value={form.QuestionsToServeCount}
                        max={Math.max(mappedCount, 0) || undefined}
                        placeholder="All assigned"
                        disabled={isReadOnly || mappedCount === 0}
                        onChange={(num) =>
                          setForm((f) => ({
                            ...f,
                            QuestionsToServeCount: num,
                          }))
                        }
                      />
                      <div className="form-text">
                        Random subset from {mappedCount} mapped question(s). Leave blank or 0
                        to use all mapped questions.
                      </div>
                    </div>

                    <div
                      className="qms-quiz-builder-modal__summary"
                      data-tour="qms-quiz-builder-summary"
                    >
                      <div className="qms-quiz-builder-modal__summary-label">Assigned</div>
                      <div className="qms-quiz-builder-modal__summary-value">
                        {mappedCount} / {availableQuestions.length}
                      </div>
                      {errors.questions && (
                        <div className="text-danger small mt-2">{errors.questions}</div>
                      )}
                    </div>
                  </aside>

                  <section className="qms-quiz-builder-modal__assign-pane">
                    {availableQuestions.length === 0 ? (
                      <div className="alert alert-info mb-0 qms-quiz-builder-modal__empty">
                        No questions yet.{" "}
                        {onGoToQuestionsTab ? (
                          <button
                            type="button"
                            className="btn btn-link p-0 align-baseline"
                            onClick={onGoToQuestionsTab}
                          >
                            Add questions in Question master tab
                          </button>
                        ) : (
                          "Add questions in Question master tab first."
                        )}
                      </div>
                    ) : (
                      <>
                        <details
                          className="qms-quiz-builder-modal__quick-help"
                          data-tour="qms-quiz-builder-quick-help"
                        >
                          <summary>Quick tips — how to assign questions</summary>
                          <ul>
                            <li>
                              <strong>Unassigned</strong> (right): click <strong>+</strong> on a row
                              or <strong>Add all</strong> to assign questions to this quiz.
                            </li>
                            <li>
                              <strong>Assigned</strong> (left): click <strong>−</strong> or{" "}
                              <strong>Remove all</strong> to unassign.
                            </li>
                            <li>
                              Click <strong>View options</strong> on any question to preview answers
                              before assigning. Correct answers show in green.
                            </li>
                            <li>Each list has its own search box. Scroll with the light scrollbar.</li>
                          </ul>
                        </details>

                        <div className="qms-quiz-builder-modal__lists-grid">
                        <div
                          className="qms-quiz-builder-modal__list-pane qms-quiz-builder-modal__list-pane--assigned"
                          data-tour="qms-quiz-builder-assigned"
                        >
                          <QuizAssignListToolbar
                            title="Assigned"
                            count={mappedCount}
                            searchValue={assignedSearch}
                            searchPlaceholder="Search..."
                            onSearchChange={setAssignedSearch}
                            bulkLabel="Remove all"
                            bulkClassName="qms-quiz-builder-modal__bulk-btn--remove"
                            onBulk={unassignAllAssigned}
                            bulkDisabled={isReadOnly || mappedCount === 0}
                          />
                          <div className="qms-quiz-builder-modal__question-list">
                            {assignedQuestions.length === 0 ? (
                              <div className="qms-quiz-builder-modal__list-empty">
                                {assignedSearchTerm
                                  ? "No assigned questions match your search."
                                  : "No questions assigned yet. Add from the unassigned list."}
                              </div>
                            ) : (
                              assignedQuestions.map((q, idx) => (
                                <QuizAssignQuestionRow
                                  key={q.QuestionId}
                                  {...getQuestionRowProps(q)}
                                  isAssigned
                                  showOptionsTour={idx === 0}
                                  onAction={unassignQuestion}
                                  actionDisabled={isReadOnly}
                                />
                              ))
                            )}
                          </div>
                        </div>

                        <div
                          className="qms-quiz-builder-modal__list-pane qms-quiz-builder-modal__list-pane--unassigned"
                          data-tour="qms-quiz-builder-unassigned"
                        >
                          <QuizAssignListToolbar
                            title="Unassigned"
                            count={Math.max(availableQuestions.length - mappedCount, 0)}
                            searchValue={unassignedSearch}
                            searchPlaceholder="Search..."
                            onSearchChange={setUnassignedSearch}
                            bulkLabel="Add all"
                            bulkClassName="qms-quiz-builder-modal__bulk-btn--add"
                            onBulk={assignAllUnassigned}
                            bulkDisabled={
                              isReadOnly || mappedCount >= availableQuestions.length
                            }
                          />
                          <div className="qms-quiz-builder-modal__question-list">
                            {unassignedQuestions.length === 0 ? (
                              <div className="qms-quiz-builder-modal__list-empty">
                                {unassignedSearchTerm
                                  ? "No unassigned questions match your search."
                                  : "All questions are assigned to this quiz."}
                              </div>
                            ) : (
                              unassignedQuestions.map((q, idx) => (
                                <QuizAssignQuestionRow
                                  key={q.QuestionId}
                                  {...getQuestionRowProps(q)}
                                  showOptionsTour={assignedQuestions.length === 0 && idx === 0}
                                  onAction={assignQuestion}
                                  actionDisabled={isReadOnly}
                                />
                              ))
                            )}
                          </div>
                        </div>
                        </div>
                      </>
                    )}
                  </section>
                </div>

                <div
                  className="modal-footer qms-quiz-builder-modal__footer py-2"
                  data-tour="qms-quiz-builder-footer"
                >
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={() => {
                      setShowModal(false);
                      resetForm();
                    }}
                  >
                    {isReadOnly ? "Close" : "Cancel"}
                  </button>
                  {!isReadOnly ? (
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      onClick={handleSave}
                    >
                      Save
                    </button>
                  ) : null}
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show" />

          <IsfPageTour
            open={showQuizTour}
            steps={quizTourSteps}
            staffId={userData?.StaffId || ""}
            markTourCompleted={markQmsQuizBuilderTourCompleted}
            onClose={() => setShowQuizTour(false)}
          />
        </QmsModalPortal>
      )}
    </>
  );
};

export default QmsQuizBuilderPanel;
